import React from "react";
import "../styles/components/TemplatePreview.css";

interface TemplatePreviewProps {
  markdown: string;
}

interface TreeNode {
  label: string;
  notes?: string;
  children: TreeNode[];
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ markdown }) => {
  const parseTree = (md: string): TreeNode | null => {
    const lines = md.split("\n");
    let inTreeSection = false;
    const stack: { node: TreeNode; indent: number }[] = [];
    let root: TreeNode | null = null;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (!inTreeSection && line.match(/^-\s+\w/) && !line.includes("```")) {
        inTreeSection = true;
      }

      if (!inTreeSection) continue;

      const indentMatch = line.match(/^(\s*)-\s+(.+)/);
      if (!indentMatch) continue;

      const indent = indentMatch[1].length;
      const content = indentMatch[2].trim();

      if (content.startsWith("Notes:")) {
        if (stack.length > 0) {
          const notes = content.substring(6).trim();
          stack[stack.length - 1].node.notes = notes;
        }
        continue;
      }

      const newNode: TreeNode = {
        label: content,
        children: [],
      };

      if (indent === 0) {
        root = newNode;
        stack.length = 0;
        stack.push({ node: newNode, indent: 0 });
      } else {
        while (stack.length > 0 && stack[stack.length - 1].indent >= indent) {
          stack.pop();
        }

        if (stack.length > 0) {
          stack[stack.length - 1].node.children.push(newNode);
          stack.push({ node: newNode, indent: indent });
        }
      }
    }

    return root;
  };

  const countNodes = (node: TreeNode | null): number => {
    if (!node) return 0;
    return 1 + node.children.reduce((sum, child) => sum + countNodes(child), 0);
  };

  const getDepth = (node: TreeNode | null): number => {
    if (!node || node.children.length === 0) return 1;
    return 1 + Math.max(...node.children.map(getDepth));
  };

  const renderNode = (node: TreeNode, level: number = 0): React.ReactNode => {
    return (
      <div
        className="preview-node"
        key={node.label}
        style={{ marginLeft: `${level * 20}px` }}
      >
        <div className="preview-node-label">
          <i
            className="pi pi-circle-fill"
            style={{ fontSize: "6px", marginRight: "8px" }}
          />
          <span>{node.label}</span>
        </div>
        {node.notes && (
          <div className="preview-node-notes" title={node.notes}>
            <i
              className="pi pi-info-circle"
              style={{ fontSize: "10px", marginRight: "4px" }}
            />
            {node.notes.substring(0, 50)}
            {node.notes.length > 50 ? "..." : ""}
          </div>
        )}
        {node.children.map((child) => renderNode(child, level + 1))}
      </div>
    );
  };

  const tree = parseTree(markdown);
  const nodeCount = countNodes(tree);
  const depth = getDepth(tree);

  // Extract NodeType
  const nodeTypeMatch = markdown.match(/^NodeType:\s*(.+?)\s*$/m);
  const nodeType = nodeTypeMatch ? nodeTypeMatch[1].trim() : "Card";

  return (
    <div className="template-preview">
      <div className="preview-header">
        <div className="preview-stat">
          <span className="preview-stat-label">Nodes:</span>
          <span className="preview-stat-value">{nodeCount}</span>
        </div>
        <div className="preview-stat">
          <span className="preview-stat-label">Depth:</span>
          <span className="preview-stat-value">{depth}</span>
        </div>
        <div className="preview-stat">
          <span className="preview-stat-label">Type:</span>
          <span className="preview-stat-value">{nodeType}</span>
        </div>
      </div>

      <div className="preview-tree">
        {tree ? (
          renderNode(tree)
        ) : (
          <div className="preview-empty">No structure found</div>
        )}
      </div>
    </div>
  );
};

export default TemplatePreview;
